import { ref, computed } from 'vue';
import { PointerAction, PointerCursor } from '@/modules/map-editor';
import type { PointerState, PointerPosition, NodeSelectionPoint } from '@/modules/map-editor';
import { useStore } from 'vuex';

/**
 * Composable for managing pointer state
 * Handles reactive state and computed properties
 */
export function usePointerState() {
  // Initial state
  const state = ref<PointerState>({
    position: { x: 0, y: 0, clientX: 0, clientY: 0, movementX: 0, movementY: 0 },
    action: PointerAction.IDLE,
    hoveredNodeId: null,
    hoveredEdgeId: null,
    hoveredSizerPoint: null,
    isMouseDown: false,
    isShiftPressed: false,
    isSpacePressed: false,
    isNodeSizing: false,
    isHoveringSelectionBox: false,
    selectionBox: null
  });

  // Store references for computed properties
  const store = useStore();

  const isEdgeConnectionsDrawingMode = computed(() =>
    store.getters["dashboardOptions/isEdgeConnectionsDrawingMode"]
  );

  const isNodeDrawingMode = computed(() =>
    store.getters["dashboardOptions/isNodeDrawingMode"]
  );

  const mapAwaitResponse = computed(() =>
    store.state.maps?.mapAwaitResponse || false
  );

  /**
   * Computed cursor class based on current state
   */
  const computedCursor = computed<PointerCursor>(() => {
    if (mapAwaitResponse.value) return PointerCursor.LOADING;
    if (isEdgeConnectionsDrawingMode.value) return PointerCursor.DRAW_CONNECTION;
    if (isNodeDrawingMode.value) return PointerCursor.DRAW_NODE;

    // Spacebar held → hand tool (pan). Grabbing while dragging, grab otherwise.
    if (state.value.isSpacePressed) {
      return state.value.isMouseDown ? PointerCursor.GRABBING : PointerCursor.GRAB;
    }

    if (state.value.hoveredSizerPoint) {
      return state.value.hoveredSizerPoint.pointer as PointerCursor;
    }

    if (state.value.isMouseDown) {
      if (state.value.action === PointerAction.DRAG_SELECTION) {
        return PointerCursor.DEFAULT;
      }
      if (state.value.action === PointerAction.DRAG_MULTI_SELECTION) {
        return PointerCursor.GRABBING;
      }
      if (state.value.action === PointerAction.DRAG_NODE ||
        state.value.action === PointerAction.RESIZE_NODE) {
        return PointerCursor.GRABBING;
      }
      return PointerCursor.GRABBING;
    }

    // Show grab cursor when hovering over multi-selection bounding box
    if (state.value.isHoveringSelectionBox) {
      return PointerCursor.GRAB;
    }

    if ((state.value.hoveredNodeId !== null && state.value.hoveredNodeId > -1) ||
        (state.value.hoveredEdgeId !== null && state.value.hoveredEdgeId > -1)) {
      return PointerCursor.POINTER;
    }

    return PointerCursor.MOVE;
  });

  function updatePosition(position: PointerPosition): void {
    state.value.position = {
      ...state.value.position,
      ...position
    };
  }

  function setHoveredNodeId(id: number | null): void {
    state.value.hoveredNodeId = id;
  }

  function setHoveredEdgeId(id: number | null): void {
    state.value.hoveredEdgeId = id;
  }

  function setHoveredSizerPoint(point: NodeSelectionPoint | null): void {
    state.value.hoveredSizerPoint = point;
  }

  function setShiftPressed(isPressed: boolean): void {
    state.value.isShiftPressed = isPressed;
  }

  function setSpacePressed(isPressed: boolean): void {
    state.value.isSpacePressed = isPressed;
  }

  function reset(): void {
    state.value = {
      position: { x: 0, y: 0, clientX: 0, clientY: 0, movementX: 0, movementY: 0 },
      action: PointerAction.IDLE,
      hoveredNodeId: null,
      hoveredEdgeId: null,
      hoveredSizerPoint: null,
      isMouseDown: false,
      isShiftPressed: false,
      isSpacePressed: false,
      isNodeSizing: false,
      isHoveringSelectionBox: false,
      selectionBox: null
    };
  }

  return {
    state,
    computedCursor,
    updatePosition,
    setHoveredNodeId,
    setHoveredEdgeId,
    setHoveredSizerPoint,
    setShiftPressed,
    setSpacePressed,
    reset
  };
}